'use client';

import { ContentFieldBlock } from '@eliadmin/core/src/components/content-fields/content-fields';
import { contentFieldKey } from '@eliadmin/core/src/api/content-fields/public-content-fields';
import { getVisibleContentFields } from '@eliadmin/core/src/utils/content-fields';
import type { TravelAgencyTour } from '@eliadmin/core/src/api/travel-agency/travel-agency-api';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { PricesTab } from './tabs/prices-tab';
import { DescriptionTab } from './tabs/description-tab';
import { ProgramTab } from './tabs/program-tab';
// import { ReviewsTab } from './tabs/reviews-tab';
import { useState, useEffect, useRef } from 'react';
import { ReviewsTab } from './tabs/reviews-tab';
import { VideosTab } from './tabs/videos-tab';
import { FaqTab } from './tabs/faq-tab';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import { getTourVideos, isTourVideoField } from '../../../utils/tour-videos';
import { getTourPhotos } from '../../../utils/tour-media';

const PROGRAM_FIELD_ID = 1;
const FAQ_TEXT_FIELD_ID = 9;

export function TourContent({ tour }: { tour: TravelAgencyTour }) {
  const { t } = useTranslations();
  const hasContentFields = Object.prototype.hasOwnProperty.call(tour, 'content_fields');
  const legacyFields = hasContentFields ? [] : (tour.text_fields ?? []);
  const faqField = legacyFields.find(field => field.id === FAQ_TEXT_FIELD_ID);
  const faqHtml = faqField?.value ?? '';
  const hasFaq = faqHtml.replace(/<[^>]*>|&nbsp;|\s/g, '').length > 0;
  const videos = getTourVideos(tour);

  const tabs = [
    {
      name: t('text3957'),
      value: 'terminy-a-ceny',
      content: <PricesTab tour={tour} />,
    },
    {
      name: t('text3958'),
      value: 'popis-zajazdu',
      content: <DescriptionTab tour={tour} />,
    },
    {
      name: 'Program',
      value: 'program',
      show: !hasContentFields && !!legacyFields[0]?.value,
      content: <ProgramTab tour={tour} />,
    },
    {
      name: t('text3959'),
      value: 'videa',
      content: (
        <VideosTab
          key={tour.id}
          videos={videos}
          tourName={tour.name}
          image={getTourPhotos(tour)[0]?.url}
        />
      ),
    },
    {
      name: t('text447'),
      value: 'recenzie',
      content: <ReviewsTab tour={tour} />,
      show: tour.references.length > 0,
    },
    {
      name: faqField?.name ?? 'Otázky a odpovede',
      value: 'otazky-a-odpovede',
      content: <FaqTab title={faqField?.name ?? 'Otázky a odpovede'} html={faqHtml} />,
      show: hasFaq,
    },
    ...getVisibleContentFields(hasContentFields ? tour : { content_fields: [] })
      .filter(field => !isTourVideoField(field))
      .map(field => ({
        name: field.name,
        // TourHeader's "View program" action targets this stable tab value.
        value: field.id === PROGRAM_FIELD_ID ? 'program' : contentFieldKey('tours', field.id),
        show: true,
        content: <ContentFieldBlock field={field} module="tours" />,
      })),
  ];

  const [activeTab, setActiveTab] = useState(tabs[0].value);
  const tabsListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleSetTab = (event: CustomEvent) => {
      const { tabValue } = event.detail;
      setActiveTab(tabValue);

      // Scroll to the TabsList component with an 80px offset
      if (tabsListRef.current) {
        const offset = 80; // Adjust the offset as needed
        const topPosition =
          tabsListRef.current.getBoundingClientRect().top + window.scrollY - offset;

        window.scrollTo({
          top: topPosition,
          behavior: 'smooth',
        });
      }
    };

    window.addEventListener('setActiveTab', handleSetTab as EventListener);

    return () => {
      window.removeEventListener('setActiveTab', handleSetTab as EventListener);
    };
  }, []);

  return (
    <section className="w-full bg-white">
      <Tabs value={activeTab} onValueChange={setActiveTab} className={cn('w-full')}>
        <TabsList
          ref={tabsListRef} // Attach the ref here for scrolling
          className={cn(
            'm-0 flex h-fit w-full flex-nowrap items-center justify-start overflow-x-scroll bg-transparent px-6 py-4 sm:py-6 md:justify-center lg:py-8',
            'hide-scroll-bar',
          )}
        >
          {tabs.map(
            tab =>
              tab.show !== false && (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className={cn(
                    'ea-tour__tabs-trigger',
                    'rounded-[16px] px-6 py-3 text-sm font-normal transition-all sm:text-base',
                  )}
                >
                  {tab.name}
                </TabsTrigger>
              ),
          )}
        </TabsList>
        {tabs
          .filter(tab => tab.show !== false)
          .map(tab => (
            <TabsContent
              key={tab.value}
              value={tab.value}
              className="w-full border-y border-y-[#E7E7E9] px-6 py-4 sm:py-6 lg:py-16"
            >
              <div className={cn('custom-container mx-auto')}>{tab.content}</div>
            </TabsContent>
          ))}
      </Tabs>
    </section>
  );
}

export function TourTabHeading({ title }: { title: string }) {
  return <h2 className="text-2xl font-bold text-foreground sm:text-3xl">{title}</h2>;
}
